import flatpickr from 'flatpickr';
import {
  formatDateISO,
  getTodayISO as getTodayISOUtil,
  parseDate,
} from '../utils/dateUtils';

export default function registerDayPlannerStore() {
  window.Alpine.store('dayPlannerStore', {
    selectedDate: '',
    loading: true, // Start with loading true to prevent flicker
    stays: {
      arrivals: [],
      currently_staying: [],
      departures: [],
    },
    tours: {},
    toastStore: null,
    flatpickrInstances: [],

    initialize(initialDate) {
      this.toastStore = Alpine.store('toastStore');
      this.selectedDate = initialDate || this.getTodayISO();
      this.loading = true; // Ensure loading is true before fetching
      this.fetchData();
    },

    initFlatpickr(element) {
      if (element) {
        // Check if this element already has a flatpickr instance
        const existingInstance = this.flatpickrInstances.find(
          (inst) => inst.element === element,
        );
        if (!existingInstance) {
          // Convert Y-m-d format to Date object for flatpickr
          const defaultDateObj = this.selectedDate
            ? new Date(this.selectedDate + 'T00:00:00') // Add time to avoid timezone issues
            : new Date();

          const instance = flatpickr(element, {
            dateFormat: 'd-m-Y',
            defaultDate: defaultDateObj,
            // Don't disable mobile - use native date picker on mobile devices
            onChange: (selectedDates, dateStr) => {
              if (selectedDates.length > 0) {
                // Convert from d-m-Y format (flatpickr display) to Y-m-d format (internal storage)
                // API accepts both formats, but we keep internal format consistent
                const date = selectedDates[0];
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                this.selectedDate = `${year}-${month}-${day}`;
                this.fetchData();
              }
            },
          });
          this.flatpickrInstances.push({ element, instance });
        }
      }
    },

    getTodayISO() {
      return getTodayISOUtil();
    },

    /**
     * Format a person count with the correct label from person_type (Adult/Child/Person).
     * @param {number} amount - count (e.g. 1, 2)
     * @param {string} personType - 'ADULT', 'CHILD', or blank/other for Person
     * @returns {string} e.g. "1 Adult", "2 Children", "1 Person"
     */
    formatPersonCount(
      amount,
      personType,
      singularLabel = 'Person',
      pluralLabel = 'People',
    ) {
      const n = amount == null ? 0 : Number(amount);
      if (n === 0) return '';
      const type = (personType || '').toUpperCase();
      if (type === 'CHILD') return n === 1 ? '1 Child' : `${n} Children`;
      if (type === 'ADULT') return n === 1 ? '1 Adult' : `${n} Adults`;
      return n === 1 ? `1 ${singularLabel}` : `${n} ${pluralLabel}`;
    },
    /** True when the person icon (fa-person) should be shown; false when a non-default person label override is in use (e.g. Cat/Cats). Pass rate_set_person_label_singular. */
    showPersonIcon(singularLabel) {
      if (singularLabel == null || singularLabel === '') return true;
      const s = String(singularLabel).trim();
      return !s || s.toLowerCase() === 'person';
    },

    onDateChanged() {
      this.fetchData();
    },

    previousDay() {
      const date = parseDate(this.selectedDate);
      if (!date) return;
      date.setDate(date.getDate() - 1);
      this.selectedDate = formatDateISO(date);
      this.updateFlatpickr();
      this.fetchData();
    },

    nextDay() {
      const date = parseDate(this.selectedDate);
      if (!date) return;
      date.setDate(date.getDate() + 1);
      this.selectedDate = formatDateISO(date);
      this.updateFlatpickr();
      this.fetchData();
    },

    goToToday() {
      this.selectedDate = this.getTodayISO();
      this.updateFlatpickr();
      this.fetchData();
    },

    updateFlatpickr() {
      // Update all flatpickr instances
      // Convert Y-m-d format to Date object for flatpickr
      const dateObj = this.selectedDate
        ? new Date(this.selectedDate + 'T00:00:00') // Add time to avoid timezone issues
        : new Date();

      this.flatpickrInstances.forEach(({ instance }) => {
        if (instance) {
          instance.setDate(dateObj, false);
        }
      });
    },

    fetchData() {
      this.loading = true;

      fetch(`/bookings_app/api/day-planner-data/?date=${this.selectedDate}`)
        .then((response) => {
          if (!response.ok) {
            return response.json().then((errorData) => {
              throw new Error(
                errorData.error || 'Failed to fetch day planner data.',
              );
            });
          }
          return response.json();
        })
        .then((data) => {
          this.stays = data.stays;
          this.tours = data.tours;
        })
        .catch((error) => {
          console.error('Error fetching day planner data:', error);
          if (this.toastStore && this.toastStore.addToast) {
            this.toastStore.addToast(
              error.message || 'Failed to load day planner data.',
              'error',
            );
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },

    exportPDF(exportType) {
      try {
        // Ensure toastStore is initialized
        if (!this.toastStore) {
          this.toastStore = Alpine.store('toastStore');
        }

        // Build the export URL with date and type
        const url = `/bookings_app/api/day-planner-export/?date=${this.selectedDate}&type=${exportType}`;

        // Create a temporary link to trigger download
        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        if (this.toastStore && this.toastStore.addToast) {
          this.toastStore.addToast('Generating PDF...', 'success');
        }
      } catch (error) {
        console.error('Error exporting PDF:', error);
        if (this.toastStore && this.toastStore.addToast) {
          this.toastStore.addToast(
            'Failed to export PDF. Please try again.',
            'error',
          );
        }
      }
    },
  });
}
